import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Clock, Target, Trash2, Flame, Zap, Trophy, Star, ChevronRight, Award, TrendingUp, BarChart3, Info, X, Globe, Bell, BellOff, Palette, Moon, Sun, Monitor } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { usePracticeStore } from '@/store/usePracticeStore';
import { useGamificationStore, ACHIEVEMENTS } from '@/store/useGamificationStore';
import { AuthModal } from '@/components/AuthModal';
import { ExamAffiliationNotice } from '@/components/ExamAffiliationNotice';
import { authApi } from '@/services/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useExamLookup } from '@/lib/api-adapters';
import { formatDate } from '@/lib/utils';
import { getContentLocale, setContentLocale, type ContentLocale } from '@/lib/contentLocale';
import { getThemePref, setThemePref, type ThemePref } from '@/lib/theme';

function isUUID(str: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}

function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="w-4 h-4 rounded-full bg-overlay-2 hover:bg-overlay-3 flex items-center justify-center transition-colors"
      >
        <Info className="w-2.5 h-2.5 text-fg-faint" />
      </button>
      {open && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-surface-2 border border-line rounded-xl p-3 shadow-xl animate-in fade-in zoom-in-95 duration-150">
          <p className="text-[11px] text-fg-muted leading-relaxed">{text}</p>
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px w-2 h-2 bg-surface-2 border-r border-b border-line rotate-45" />
        </div>
      )}
    </div>
  );
}

function getAchievementProgress(
  id: string,
  state: { quizCount: number; streak: number; totalXP: number; level: number; examsExplored: string[]; bestScore: number }
): { current: number; target: number } | null {
  switch (id) {
    case 'first-quiz': return { current: Math.min(state.quizCount, 1), target: 1 };
    case 'streak-3': return { current: Math.min(state.streak, 3), target: 3 };
    case 'streak-5': return { current: Math.min(state.streak, 5), target: 5 };
    case 'perfect': return { current: state.bestScore >= 100 ? 1 : 0, target: 1 };
    case 'level-5': return { current: Math.min(state.level, 5), target: 5 };
    case 'xp-1000': return { current: Math.min(state.totalXP, 1000), target: 1000 };
    case 'ten-quizzes': return { current: Math.min(state.quizCount, 10), target: 10 };
    case 'explorer': return { current: Math.min(state.examsExplored.length, 3), target: 3 };
    default: return null;
  }
}

const NOTIF_KEY = 'certexam-notifications';

function getNotifPref(): boolean {
  return localStorage.getItem(NOTIF_KEY) !== 'off';
}
function setNotifPref(on: boolean) {
  localStorage.setItem(NOTIF_KEY, on ? 'on' : 'off');
}

export function ProfilePage() {
  const navigate = useNavigate();
  const [locale, setLocale] = useState<ContentLocale>(getContentLocale());
  const { isAuthenticated, user, logout, isGuestSession } = useAuthStore();
  const { completedSessions, clearHistory } = usePracticeStore();
  const { totalXP, level, streak, achievements, getXPProgress, quizCount, examsExplored } = useGamificationStore();
  const { byId: examsById } = useExamLookup();
  const [showAuth, setShowAuth] = useState(false);
  const [notifications, setNotifications] = useState(getNotifPref());
  const [theme, setTheme] = useState(getThemePref());
  const [showDelete, setShowDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const xpProgress = getXPProgress();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await authApi.deleteAccount(isGuestSession ? undefined : deletePassword);
      logout();
      toast.success(locale === 'en' ? 'Your account has been deleted.' : 'Votre compte a été supprimé.');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(', ')
          : msg || (locale === 'en' ? 'Unable to delete account' : 'Impossible de supprimer le compte'),
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleLocaleChange = (newLocale: ContentLocale) => {
    setContentLocale(newLocale);
    setLocale(newLocale);
    window.location.reload();
  };

  const handleNotifToggle = () => {
    const next = !notifications;
    setNotifPref(next);
    setNotifications(next);
  };

  const handleThemeChange = (v: ThemePref) => {
    setThemePref(v);
    setTheme(v);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-green-500/10';
    if (score >= 60) return 'bg-yellow-500/10';
    return 'bg-red-500/10';
  };

  const unlockedAchievements = ACHIEVEMENTS.filter(a => achievements.includes(a.id));
  const lockedAchievements = ACHIEVEMENTS.filter(a => !achievements.includes(a.id));

  const avgScore = completedSessions.length > 0
    ? Math.round(completedSessions.reduce((s, c) => s + c.score, 0) / completedSessions.length)
    : 0;

  const bestScore = completedSessions.length > 0
    ? Math.max(...completedSessions.map(s => s.score))
    : 0;

  const achievementState = { quizCount, streak, totalXP, level, examsExplored, bestScore };

  const t = {
    profile: locale === 'en' ? 'Profile' : 'Profil',
    manageAccount: locale === 'en' ? 'Manage your account' : 'Gérez votre compte',
    level: locale === 'en' ? 'Level' : 'Niv.',
    guest: locale === 'en' ? 'Guest' : 'Invité',
    createAccount: locale === 'en' ? 'Create an account' : 'Créer un compte',
    xpToNext: locale === 'en' ? 'XP to level' : 'XP pour le niveau',
    xpTotal: 'XP Total',
    levelLabel: locale === 'en' ? 'Level' : 'Niveau',
    streakLabel: 'Streak',
    avgScore: locale === 'en' ? 'Avg. score' : 'Score moy.',
    achievements: locale === 'en' ? 'Achievements' : 'Succès',
    unlockAchievements: locale === 'en' ? 'Complete quizzes to earn achievements' : 'Complétez des quiz pour débloquer des succès',
    settings: locale === 'en' ? 'Settings' : 'Paramètres',
    language: locale === 'en' ? 'Language' : 'Langue',
    notif: 'Notifications',
    themeLabel: locale === 'en' ? 'Theme' : 'Thème',
    logout: locale === 'en' ? 'Log out' : 'Se déconnecter',
    deleteAccount: locale === 'en' ? 'Delete my account' : 'Supprimer mon compte',
    deleteWarn: locale === 'en'
      ? 'This permanently deletes your profile, progress and personal data. This cannot be undone.'
      : 'Cette action supprime définitivement votre profil, votre progression et vos données personnelles. Elle est irréversible.',
    password: locale === 'en' ? 'Current password' : 'Mot de passe actuel',
    deleteConfirm: locale === 'en' ? 'I understand this is permanent' : 'Je comprends que c’est définitif',
    cancel: locale === 'en' ? 'Cancel' : 'Annuler',
    legal: locale === 'en' ? 'Legal' : 'Mentions légales',
    history: locale === 'en' ? 'History' : 'Historique',
    clear: locale === 'en' ? 'Clear' : 'Effacer',
    noExams: locale === 'en' ? 'No exams taken yet' : 'Aucun examen passé',
    startQuiz: locale === 'en' ? 'Start a quiz to see your results here' : 'Lancez un quiz pour voir vos résultats ici',
    unknownExam: locale === 'en' ? 'Unknown exam' : 'Examen inconnu',
    xpInfo: locale === 'en'
      ? 'Total experience points earned from quizzes and simulations. XP determines your level.'
      : 'Points d\'expérience totaux gagnés via les quiz et simulations. Les XP déterminent votre niveau.',
    levelInfo: locale === 'en'
      ? 'Your current level based on accumulated XP. Higher levels unlock new achievements.'
      : 'Votre niveau actuel basé sur les XP accumulés. Les niveaux supérieurs débloquent de nouveaux succès.',
    streakInfo: locale === 'en'
      ? 'Number of consecutive days you completed at least one quiz. Keep your streak alive!'
      : 'Nombre de jours consécutifs où vous avez complété au moins un quiz. Maintenez votre série !',
    avgInfo: locale === 'en'
      ? 'Your average score across all completed quizzes and simulations.'
      : 'Votre score moyen sur tous les quiz et simulations complétés.',
    dark: locale === 'en' ? 'Dark' : 'Sombre',
    light: locale === 'en' ? 'Light' : 'Clair',
    system: locale === 'en' ? 'System' : 'Système',
    unlocked: locale === 'en' ? 'Unlocked!' : 'Débloqué !',
  };

  return (
    <div className="flex flex-col h-full bg-app pb-20 lg:pb-0">
      {showAuth && <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />}

      <div className="flex-shrink-0 px-4 sm:px-5 lg:px-8 pt-5 sm:pt-6 pb-3">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-fg">{t.profile}</h1>
          <p className="text-xs sm:text-sm text-fg-subtle mt-0.5">{t.manageAccount}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-5 lg:px-8 pb-6">
        <div className="max-w-6xl mx-auto">

          {/* Top section: User Card + Stats */}
          <div className="lg:grid lg:grid-cols-[1fr_1fr] lg:gap-4 space-y-4 lg:space-y-0 mb-4">

            {/* User Card */}
            <div className="bg-gradient-to-br from-surface to-surface-2 rounded-2xl border border-line p-5 lg:p-6 flex flex-col">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-500/20">
                    <span className="text-2xl lg:text-3xl font-bold text-fg">
                      {isAuthenticated && user ? user.name[0].toUpperCase() : '?'}
                    </span>
                  </div>
                  {isAuthenticated && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-surface rounded-full flex items-center justify-center border-2 border-surface">
                      <div className="w-4 h-4 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center">
                        <span className="text-[8px] font-bold text-black">{level}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {isAuthenticated && user ? (
                    <>
                      <h2 className="text-base lg:text-lg font-semibold text-fg truncate">{user.name}</h2>
                      <p className="text-xs lg:text-sm text-fg-faint truncate">{user.email}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[10px] lg:text-xs px-2.5 py-0.5 bg-yellow-500/10 text-yellow-400 rounded-md font-medium border border-yellow-500/15">
                          {t.level} {level}
                        </span>
                        {streak > 0 && (
                          <span className="text-[10px] lg:text-xs px-2.5 py-0.5 bg-orange-500/10 text-orange-400 rounded-md font-medium border border-orange-500/15 flex items-center gap-0.5">
                            <Flame className="w-3 h-3" /> {streak}j
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="text-base lg:text-lg font-semibold text-fg-muted">{t.guest}</h2>
                      <button
                        onClick={() => setShowAuth(true)}
                        className="text-xs lg:text-sm text-green-400 hover:text-green-300 transition-colors flex items-center gap-1 mt-1"
                      >
                        {t.createAccount}
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="mt-4 lg:mt-auto lg:pt-4 border-t border-line lg:border-t">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <Star className="w-3.5 h-3.5 text-yellow-400" />
                    <span className="text-xs font-semibold text-fg">{totalXP.toLocaleString()} XP</span>
                  </div>
                  <span className="text-[10px] text-fg-faint">
                    {xpProgress.current}/{xpProgress.needed}
                  </span>
                </div>
                <div className="h-2 bg-overlay-2 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-yellow-500 to-amber-500 rounded-full transition-all duration-700"
                    style={{ width: `${xpProgress.percent}%` }}
                  />
                </div>
                <p className="text-[10px] text-fg-faint mt-1 text-right">
                  {xpProgress.needed - xpProgress.current} {t.xpToNext} {level + 1}
                </p>
              </div>
            </div>

            {/* Stats Grid with info tooltips */}
            <div className="grid grid-cols-2 gap-3 lg:gap-4 content-start">
              <StatTile icon={<Zap className="w-4 h-4 lg:w-5 lg:h-5" />} value={totalXP.toLocaleString()} label={t.xpTotal} accent="yellow" info={t.xpInfo} />
              <StatTile icon={<Trophy className="w-4 h-4 lg:w-5 lg:h-5" />} value={String(level)} label={t.levelLabel} accent="blue" info={t.levelInfo} />
              <StatTile icon={<Flame className="w-4 h-4 lg:w-5 lg:h-5" />} value={String(streak)} label={t.streakLabel} accent="orange" info={t.streakInfo} />
              <StatTile icon={<BarChart3 className="w-4 h-4 lg:w-5 lg:h-5" />} value={avgScore > 0 ? `${avgScore}%` : '—'} label={t.avgScore} accent="green" info={t.avgInfo} />
            </div>
          </div>

          {/* Middle section: Achievements + History */}
          <div className="lg:grid lg:grid-cols-[340px_1fr] lg:gap-4 space-y-4 lg:space-y-0 mb-4">

            {/* Left column: Settings (en premier, toujours visible) + Succès */}
            <div className="flex flex-col gap-4">
              {/* Achievements with progress */}
              <div className="bg-surface rounded-2xl border border-line p-4 lg:p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-yellow-400" />
                    <p className="text-xs lg:text-sm font-semibold text-fg">{t.achievements}</p>
                  </div>
                  <span className="text-[10px] text-fg-faint">{unlockedAchievements.length}/{ACHIEVEMENTS.length}</span>
                </div>

                {/* Liste plafonnée + défilante : le bloc ne s'étire plus sur toute la page */}
                <div className="max-h-72 overflow-y-auto -mr-2 pr-2">
                {unlockedAchievements.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {unlockedAchievements.map(a => (
                      <div key={a.id} className="flex items-center gap-3 p-2.5 bg-overlay-1 rounded-xl">
                        <span className="text-xl">{a.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-fg truncate">{a.name}</p>
                          <p className="text-[10px] text-fg-faint truncate">{a.description}</p>
                        </div>
                        <span className="text-[9px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                          {t.unlocked}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {unlockedAchievements.length === 0 && (
                  <p className="text-xs text-fg-faint text-center py-3 mb-3">
                    {t.unlockAchievements}
                  </p>
                )}

                {lockedAchievements.length > 0 && (
                  <div className="space-y-1.5">
                    {lockedAchievements.map(a => {
                      const progress = getAchievementProgress(a.id, achievementState);
                      const pct = progress ? Math.min(100, (progress.current / progress.target) * 100) : 0;

                      return (
                        <div key={a.id} className="flex items-center gap-3 p-2.5 bg-overlay-1 rounded-xl opacity-60">
                          <span className="text-xl grayscale">{a.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-fg-subtle truncate">{a.name}</p>
                            <p className="text-[10px] text-fg-faint truncate">{a.description}</p>
                            {progress && (
                              <div className="flex items-center gap-2 mt-1.5">
                                <div className="flex-1 h-1 bg-overlay-2 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-yellow-500/50 rounded-full transition-all duration-500"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span className="text-[9px] text-fg-faint font-mono flex-shrink-0">
                                  {progress.current}/{progress.target}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>

              {/* Settings — Language, Notifications, Theme.
                  order-first : remonte au-dessus des Succès pour rester
                  visible sans scroller (les listes sont longues). */}
              <div className="order-first bg-surface rounded-2xl border border-line p-4 lg:p-5">
                <p className="text-xs lg:text-sm font-semibold text-fg mb-3">{t.settings}</p>
                <div className="space-y-3">

                  {/* Language */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5 text-fg-faint" />
                      <span className="text-xs text-fg-subtle">{t.language}</span>
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => handleLocaleChange('fr')}
                        className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${
                          locale === 'fr'
                            ? 'bg-green-500/15 text-green-400 border border-green-500/25'
                            : 'bg-overlay-1 text-fg-subtle border border-line hover:bg-overlay-2'
                        }`}
                      >
                        🇫🇷 Français
                      </button>
                      <button
                        onClick={() => handleLocaleChange('en')}
                        className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${
                          locale === 'en'
                            ? 'bg-green-500/15 text-green-400 border border-green-500/25'
                            : 'bg-overlay-1 text-fg-subtle border border-line hover:bg-overlay-2'
                        }`}
                      >
                        🇬🇧 English
                      </button>
                    </div>
                  </div>

                  <div className="h-px bg-overlay-1" />

                  {/* Notifications */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {notifications ? <Bell className="w-3.5 h-3.5 text-fg-faint" /> : <BellOff className="w-3.5 h-3.5 text-fg-faint" />}
                      <span className="text-xs text-fg-subtle">{t.notif}</span>
                    </div>
                    <button
                      onClick={handleNotifToggle}
                      className={`relative w-10 h-5.5 rounded-full transition-colors ${
                        notifications ? 'bg-green-500' : 'bg-overlay-3'
                      }`}
                    >
                      <div className={`absolute top-0.5 w-4.5 h-4.5 bg-white rounded-full shadow transition-transform ${
                        notifications ? 'translate-x-[22px]' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <div className="h-px bg-overlay-1" />

                  {/* Theme */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Palette className="w-3.5 h-3.5 text-fg-faint" />
                      <span className="text-xs text-fg-subtle">{t.themeLabel}</span>
                    </div>
                    <div className="flex gap-1.5">
                      {([
                        { value: 'dark' as const, label: t.dark, icon: <Moon className="w-3 h-3" /> },
                        { value: 'light' as const, label: t.light, icon: <Sun className="w-3 h-3" /> },
                        { value: 'system' as const, label: t.system, icon: <Monitor className="w-3 h-3" /> },
                      ]).map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => handleThemeChange(opt.value)}
                          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium rounded-lg transition-all ${
                            theme === opt.value
                              ? 'bg-green-500/15 text-green-400 border border-green-500/25'
                              : 'bg-overlay-1 text-fg-subtle border border-line hover:bg-overlay-2'
                          }`}
                        >
                          {opt.icon}
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {isAuthenticated && (
                  <>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center justify-center gap-2 py-2.5 mt-4 bg-red-500/5 border border-red-500/10 rounded-xl text-sm font-medium text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      {t.logout}
                    </button>
                    <button
                      onClick={() => setShowDelete(true)}
                      className="w-full flex items-center justify-center gap-2 py-2.5 mt-2 bg-transparent border border-red-500/10 rounded-xl text-xs font-medium text-red-400/50 hover:text-red-400 hover:border-red-500/25 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {t.deleteAccount}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Right column: History */}
            <div className="bg-surface rounded-2xl border border-line p-4 lg:p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-fg-faint" />
                  <p className="text-xs lg:text-sm font-semibold text-fg">{t.history}</p>
                  {completedSessions.length > 0 && (
                    <span className="text-[10px] text-fg-faint bg-overlay-1 px-1.5 py-0.5 rounded">
                      {completedSessions.length}
                    </span>
                  )}
                </div>
                {completedSessions.length > 0 && (
                  <button
                    onClick={clearHistory}
                    className="flex items-center gap-1 text-[10px] text-red-400/60 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    {t.clear}
                  </button>
                )}
              </div>

              {completedSessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 lg:py-16">
                  <Clock className="w-10 h-10 text-fg-faint mb-3" />
                  <p className="text-sm text-fg-faint">{t.noExams}</p>
                  <p className="text-xs text-fg-faint mt-1">{t.startQuiz}</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[440px] overflow-y-auto -mr-2 pr-2">
                  {completedSessions.slice(0, 15).map((session, i) => {
                    const exam = examsById[session.examId];
                    const displayName = exam?.name
                      || (isUUID(session.examId) ? t.unknownExam : session.examId);
                    return (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl hover:bg-overlay-1 transition-colors group">
                        <div className={`w-10 h-10 ${getScoreBg(session.score)} rounded-xl flex items-center justify-center text-lg flex-shrink-0`}>
                          {exam?.icon || '📝'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs lg:text-sm font-medium text-fg truncate">{displayName}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-[10px] lg:text-xs text-fg-faint">{formatDate(new Date(session.completedAt), locale)}</p>
                            <span className="text-[10px] text-fg-faint">·</span>
                            <p className="text-[10px] lg:text-xs text-fg-faint">{session.correctCount}/{session.totalQuestions}</p>
                          </div>
                        </div>
                        <div className={`px-2.5 py-1 rounded-lg ${getScoreBg(session.score)}`}>
                          <span className={`text-xs lg:text-sm font-bold ${getScoreColor(session.score)}`}>
                            {session.score}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <p className="text-center text-[10px] text-fg-faint pt-2 pb-2">
            Exam Copilot v1.0.0 ·{' '}
            <Link to="/legal/cgu" className="underline underline-offset-2 hover:text-fg-subtle">{t.legal}</Link>
          </p>
          <ExamAffiliationNotice variant="banner" />
        </div>
      </div>

      {showDelete && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowDelete(false)} />
          <div className="relative w-full max-w-md bg-surface border border-line rounded-2xl p-5 space-y-3">
            <h3 className="text-base font-bold text-fg">{t.deleteAccount}</h3>
            <p className="text-xs text-fg-subtle leading-relaxed">{t.deleteWarn}</p>
            {!isGuestSession && (
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder={t.password}
                className="w-full px-4 py-3 bg-app border border-line text-fg rounded-xl text-sm"
              />
            )}
            <label className="flex items-start gap-2 text-xs text-fg-subtle">
              <input
                type="checkbox"
                checked={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.checked)}
                className="mt-0.5"
              />
              {t.deleteConfirm}
            </label>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowDelete(false)}
                className="flex-1 py-2.5 rounded-xl text-sm border border-line text-fg-subtle"
              >
                {t.cancel}
              </button>
              <button
                type="button"
                disabled={deleting || !deleteConfirm || (!isGuestSession && !deletePassword)}
                onClick={handleDeleteAccount}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-red-500/15 border border-red-500/25 text-red-400 disabled:opacity-50"
              >
                {deleting ? '…' : t.deleteAccount}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const STAT_ACCENTS = {
  yellow: { bg: 'bg-yellow-500/[0.07]', border: 'border-yellow-500/10', iconBg: 'bg-yellow-500/15', text: 'text-yellow-400' },
  blue: { bg: 'bg-blue-500/[0.07]', border: 'border-blue-500/10', iconBg: 'bg-blue-500/15', text: 'text-blue-400' },
  orange: { bg: 'bg-orange-500/[0.07]', border: 'border-orange-500/10', iconBg: 'bg-orange-500/15', text: 'text-orange-400' },
  green: { bg: 'bg-green-500/[0.07]', border: 'border-green-500/10', iconBg: 'bg-green-500/15', text: 'text-green-400' },
} as const;

function StatTile({ icon, value, label, accent, info }: { icon: React.ReactNode; value: string; label: string; accent: keyof typeof STAT_ACCENTS; info: string }) {
  const a = STAT_ACCENTS[accent];
  return (
    <div className={`${a.bg} ${a.border} rounded-2xl p-4 lg:p-5 border transition-colors hover:border-line relative`}>
      <div className="absolute top-3 right-3">
        <InfoTooltip text={info} />
      </div>
      <div className={`w-8 h-8 lg:w-9 lg:h-9 ${a.iconBg} ${a.text} rounded-lg flex items-center justify-center mb-2.5`}>
        {icon}
      </div>
      <p className="text-xl lg:text-2xl font-bold text-fg leading-tight">{value}</p>
      <p className="text-[10px] lg:text-xs text-fg-faint mt-0.5">{label}</p>
    </div>
  );
}
